import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Button, Badge, Form, Modal, ButtonGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import { useAuth } from '../context/AuthContext';
import CreditNoteModal from './CreditNoteModal';
import { formatDay, formatDateTime } from '../utils/dates';

const TZ = 'America/Santo_Domingo';
const dateTime = (d) => formatDateTime(d);
const bareDate = (d) => formatDay(d, null); // deliveryEta is a bare date
const todayIso = () => new Date().toLocaleDateString('en-CA', { timeZone: TZ });

const STATUS = {
  pending: { label: 'Nuevo', bg: 'warning' },
  confirmed: { label: 'Confirmado', bg: 'info' },
  en_planta: { label: 'Llegó a planta', bg: 'primary' },
  delivered: { label: 'Entregado', bg: 'success' },
  cancelled: { label: 'Cancelado', bg: 'secondary' },
};

const VIEWS = [
  { key: 'open', label: 'Por atender', hint: 'Pedidos que los clientes hicieron en CG Connect o por WhatsApp y que todavía no tienen conduce ni factura. Confírmalos o cancélalos, y crea su conduce o su factura.' },
  { key: 'por_entregar', label: 'Por entregar', hint: 'Pedidos con conduce o factura que el cliente todavía no ha recibido. Marcar un conduce como entregado también entrega su pedido; una venta solo con factura se marca aquí.' },
  { key: 'devoluciones', label: 'Devoluciones', hint: 'Tanques que el cliente devuelve. Una devolución "solicitada" espera a que los tanques lleguen a planta (se confirma en Manifold). Cuando llegan y el pedido ya estaba facturado, emite la nota de crédito (viene pre-llenada) o indica por qué no hace falta.' },
  { key: 'all', label: 'Todos', hint: 'Todos los pedidos, incluidos los creados para cada conduce o factura (ventas), los entregados y los cancelados.' },
];

const hasDoc = (o, type) => (o.documents || []).some(d => d.type === type);

// How the tanks move (backend services/pedidoProgress.js): chosen by the client in CG Connect, changeable here.
const EMPTIES = { we_collect: 'Los recogemos', client_drops: 'El cliente los trae', none: 'Sin vacíos', undecided: 'Por coordinar' };
const FILLED = { we_deliver: 'Se los llevamos', client_picks: 'El cliente los retira', undecided: 'Por coordinar' };
const legsOf = (o) => ({ empties: o.legs?.empties || 'undecided', filled: o.legs?.filled || 'undecided' });

// What the client kept from the plant's despachos (backend pedidoProgress envioTotals) — the most a new devolución can be.
const keptOf = (o) => (o.envios || []).filter(e => e.status === 'entregado').reduce((s, e) => s + Math.min(e.received ?? e.sent, e.sent - (e.returned || 0)), 0);
const returnableOf = (o) => keptOf(o) - (o.devoluciones || []).filter(d => d.status === 'solicitada').reduce((s, d) => s + d.quantity, 0);

// The credit note for returned tanks, line by line: the returned quantity goes on the factura's tank lines, in order.
function creditSuggestion(invoice, dev, pedidoNumber) {
  let left = dev.receivedQuantity ?? dev.quantity;
  const quantities = {};
  invoice.items.forEach((it, i) => {
    if (left <= 0 || it.quantity <= 0 || (it.unidadMedida != null && it.unidadMedida !== 9)) return;
    const take = Math.min(it.quantity, left);
    quantities[i] = take;
    left -= take;
  });
  const why = dev.kind === 'faltante' ? 'no entregados' : 'devueltos';
  return { code: 3, reason: `${dev.receivedQuantity ?? dev.quantity} tanques ${why} (pedido ${pedidoNumber})`.slice(0, 90), quantities };
}

// Conduces → "Pedidos": every order, whether the client placed it in CG Connect or it was created for a conduce or
// factura made without one (backend services/pedidoDocuments.js). What happens here is what the client sees in the app.
const OrderRequestsPanel = ({ onCountChange, counts }) => {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const isAdmin = user?.role === 'admin';
  const [view, setView] = useState('open');
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busyId, setBusyId] = useState('');
  const [confirming, setConfirming] = useState(null); // { order, eta }
  const [cancelling, setCancelling] = useState(null); // { order, reason }
  const [editingLegs, setEditingLegs] = useState(null); // { order, empties, filled }
  const [newDevolucion, setNewDevolucion] = useState(null); // { order, quantity, reason }
  const [crediting, setCrediting] = useState(null); // { order, dev, invoice }
  const [noBilling, setNoBilling] = useState(null); // { order, dev, comment }

  const load = useCallback(() => API.get('/order-requests', { params: { status: view } })
    .then(r => setOrders(r.data))
    .catch(() => setError('No se pudieron cargar los pedidos.')), [view]);
  useEffect(() => { setOrders(null); load(); }, [load]);

  const act = async (id, fn, ok) => {
    setBusyId(id); setError(''); setNotice('');
    try { await fn(); setNotice(ok); await load(); onCountChange?.(); return true; }
    catch (err) { setError(err.response?.data?.message || 'La acción falló.'); return false; }
    finally { setBusyId(''); }
  };

  const submitConfirm = async () => {
    const { order, eta } = confirming;
    if (await act(order._id, () => API.post(`/order-requests/${order._id}/confirm`, { deliveryEta: eta || null }), `Pedido ${order.number} confirmado. El cliente ya lo ve en la app.`)) setConfirming(null);
  };
  const submitCancel = async () => {
    const { order, reason } = cancelling;
    if (await act(order._id, () => API.post(`/order-requests/${order._id}/cancel`, { reason }), `Pedido ${order.number} cancelado.`)) setCancelling(null);
  };
  const submitLegs = async () => {
    const { order, empties, filled } = editingLegs;
    if (await act(order._id, () => API.post(`/order-requests/${order._id}/legs`, { empties, filled }), `Forma de entrega del pedido ${order.number} actualizada. El cliente lo ve en la app.`)) setEditingLegs(null);
  };
  const submitDevolucion = async () => {
    const { order, quantity, reason } = newDevolucion;
    if (await act(order._id, () => API.post(`/order-requests/${order._id}/devoluciones`, { quantity: Number(quantity), reason }), `Devolución de ${quantity} tanques del pedido ${order.number} enviada a la planta. Se descuenta cuando lleguen.`)) setNewDevolucion(null);
  };
  const cancelDev = (o, d) => {
    if (!window.confirm(`¿Cancelar la devolución de ${d.quantity} tanques del pedido ${o.number}?`)) return;
    act(o._id, () => API.post(`/order-requests/${o._id}/devoluciones/${d.id}/cancel`), 'Devolución cancelada.');
  };
  const openCredit = async (o, d) => {
    setError('');
    try {
      const { data } = await API.get(`/invoices/${d.billing.invoiceId}`);
      setCrediting({ order: o, dev: d, invoice: data });
    } catch (err) {
      setError('No se pudo cargar la factura.');
    }
  };
  const creditIssued = ({ note, asked }) => {
    const { order, dev } = crediting;
    act(order._id, () => API.post(`/order-requests/${order._id}/devoluciones/${dev.id}/billing`, asked ? { status: 'en_aprobacion' } : { status: 'resuelta', noteNumber: note.number }),
      asked ? 'Nota de crédito pedida a un administrador.' : `Nota de crédito ${note.number} registrada en la devolución.`);
  };
  const submitNoBilling = async () => {
    const { order, dev, comment } = noBilling;
    if (await act(order._id, () => API.post(`/order-requests/${order._id}/devoluciones/${dev.id}/billing`, { status: 'no_aplica', comment }), 'Anotado: esta devolución no requiere nota de crédito.')) setNoBilling(null);
  };
  const deliver = (o) => {
    if (!window.confirm(`¿El cliente ya recibió el pedido ${o.number}?${o.conduce ? ` Su conduce ${o.conduce.number} también quedará como entregado.` : ''}`)) return;
    act(o._id, () => API.post(`/order-requests/${o._id}/deliver`), `Pedido ${o.number} entregado. El cliente ya lo ve en la app.`);
  };
  const undeliver = (o) => {
    if (!window.confirm(`¿Deshacer la entrega del pedido ${o.number}?${o.conduce ? ` Su conduce ${o.conduce.number} vuelve a "por entregar".` : ''}`)) return;
    act(o._id, () => API.post(`/order-requests/${o._id}/undeliver`), `Entrega del pedido ${o.number} deshecha.`);
  };

  // Fulfilling (conduce / factura) only once the pedido is actually confirmed — a still-"Nuevo" one hasn't been
  // reviewed for price/availability yet, so making its documents before that would jump the gun.
  const needsConduce = (o) => ['confirmed', 'en_planta'].includes(o.status) && !o.conduce;
  const invoiceable = (o) => ['confirmed', 'en_planta', 'delivered'].includes(o.status) && !hasDoc(o, 'factura');
  const confirmable = (o) => ['pending', 'confirmed'].includes(o.status) && !o.documents?.length;
  // Tanks already at the plant don't stop a cancellation: the plant is told they're still there, whose they are.
  const cancellable = (o) => ['pending', 'confirmed', 'en_planta'].includes(o.status) && !o.documents?.length;
  const deliverable = (o) => o.documents?.length > 0 && !['delivered', 'cancelled'].includes(o.status);

  const countFor = { open: counts?.open, por_entregar: counts?.porEntregar, devoluciones: counts?.devoluciones };
  const current = VIEWS.find(v => v.key === view);

  return (
    <Card className="shadow-sm p-3">
      {error && <div className="alert alert-danger">{error}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <ButtonGroup size="sm">
          {VIEWS.map(v => (
            <Button key={v.key} variant={view === v.key ? 'primary' : 'outline-secondary'} onClick={() => setView(v.key)}>
              {v.label}{countFor[v.key] > 0 ? ` (${countFor[v.key]})` : ''}
            </Button>
          ))}
        </ButtonGroup>
      </div>
      <div className="small text-muted mb-3">{current.hint}</div>
      {orders === null ? <div className="text-muted py-3">Cargando...</div> : (
        <div className="table-responsive">
          <Table hover className="mb-0 align-middle">
            <thead>
              <tr><th>No.</th><th>Cliente</th><th>Recibido</th><th>Productos</th><th>Documentos</th><th>Estado</th><th /></tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o._id}>
                  <td>
                    <strong>{o.number}</strong>
                    <div className="small text-muted">{o.origin === 'auto' ? 'Venta' : o.origin === 'whatsapp' ? 'WhatsApp' : 'CG Connect'}</div>
                  </td>
                  <td>
                    {o.client?.name || '—'}
                    <div className="small text-muted">{o.requestedBy}{o.client?.phone ? `${o.requestedBy ? ' · ' : ''}${o.client.phone}` : ''}</div>
                  </td>
                  <td className="small">{dateTime(o.createdAt)}</td>
                  <td className="small">
                    {o.items.map((it, i) => <div key={i}>{it.quantity} × {it.description}</div>)}
                    {o.notes && <div className="text-muted fst-italic">“{o.notes}”</div>}
                    {o.deliveryAddress && <div className="text-muted"><i className="bi bi-geo-alt" /> {o.deliveryAddress}</div>}
                    {o.contactInfo && <div className="text-muted"><i className="bi bi-person-lines-fill" /> {o.contactInfo}</div>}
                    <div className="text-muted" title="Cómo se mueven los tanques">
                      <i className="bi bi-arrow-left-right" /> Vacíos: {EMPTIES[legsOf(o).empties]} · Llenos: {FILLED[legsOf(o).filled]}
                    </div>
                  </td>
                  <td className="small">
                    {(o.documents || []).map(d => (
                      <div key={d.id}>
                        {d.type === 'conduce' ? 'Conduce' : 'Factura'} {d.number}
                        {d.type === 'conduce' && o.conduce?.status === 'delivered' && <span className="text-muted"> · entregado</span>}
                      </div>
                    ))}
                    {!o.documents?.length && <span className="text-muted">—</span>}
                    {(o.envios || []).filter(e => e.status !== 'anulado').map(e => (
                      <div key={e.manifoldDispatchId} className="text-muted">
                        <i className={`bi ${e.method === 'retiro' ? 'bi-shop' : 'bi-truck'}`} /> {e.method === 'retiro' ? 'Retiró' : 'Salieron'} {e.sent}
                        {e.status === 'entregado'
                          ? (e.method === 'retiro' ? '' : <> · recibió {e.received ?? e.sent}{(e.received ?? e.sent) < e.sent && <span className="text-warning"> (faltan {e.sent - e.received})</span>}</>)
                          : ' · en camino'}
                        {e.returned > 0 && <> · volvieron {e.returned}</>}
                      </div>
                    ))}
                    {(o.devoluciones || []).filter(d => d.status !== 'cancelada').map(d => (
                      <div key={d.id} className="mt-1">
                        <i className="bi bi-arrow-return-left" /> {d.kind === 'faltante' ? 'Faltante' : 'Devolución'}{' '}
                        {d.status === 'solicitada'
                          ? <><span className="text-warning">{d.quantity} por llegar</span>{d.reason ? ` — ${d.reason}` : ''}
                            <Button size="sm" variant="link" className="p-0 ml-1" disabled={busyId === o._id} onClick={() => cancelDev(o, d)}>cancelar</Button></>
                          : <>{d.receivedQuantity ?? d.quantity} en planta
                            {d.billing?.status === 'pendiente' && (
                              <span className="ml-1">
                                · <Button size="sm" variant="link" className="p-0" onClick={() => openCredit(o, d)}>Nota de crédito ({d.billing.invoiceNumber})</Button>
                                {' / '}<Button size="sm" variant="link" className="p-0" onClick={() => setNoBilling({ order: o, dev: d, comment: '' })}>no requiere</Button>
                              </span>
                            )}
                            {d.billing?.status === 'en_aprobacion' && <span className="text-muted"> · nota de crédito en aprobación</span>}
                            {d.billing?.status === 'resuelta' && <span className="text-muted"> · NC {d.billing.noteNumber}</span>}
                          </>}
                      </div>
                    ))}
                  </td>
                  <td>
                    <Badge bg={STATUS[o.status]?.bg || 'secondary'}>{STATUS[o.status]?.label || o.status}</Badge>
                    {o.status === 'confirmed' && o.deliveryEta && !o.documents?.length && <div className="small text-muted">Entrega {bareDate(o.deliveryEta)}</div>}
                    {o.arrivedQuantity != null && (
                      <div className="small text-muted">
                        {o.arrivedQuantity} recibidos en planta{o.origin !== 'auto' && o.arrivedQuantity !== o.items.reduce((s, it) => s + it.quantity, 0) ? ` (pidió ${o.items.reduce((s, it) => s + it.quantity, 0)})` : ''}
                      </div>
                    )}
                    {o.status === 'delivered' && o.deliveredAt && <div className="small text-muted">{dateTime(o.deliveredAt)}</div>}
                    {o.status === 'cancelled' && o.cancelReason && <div className="small text-muted">{o.cancelReason}</div>}
                  </td>
                  <td className="text-end">
                    <div className="d-flex gap-2 justify-content-end flex-wrap">
                      {needsConduce(o) && (
                        <Button size="sm" onClick={() => navigate(`/conduces/new?pedido=${o._id}`)}><i className="bi bi-truck" /> Crear conduce</Button>
                      )}
                      {invoiceable(o) && (
                        <Button size="sm" variant="outline-primary" onClick={() => navigate(`/invoices/new?pedido=${o._id}`)}><i className="bi bi-receipt" /> Facturar</Button>
                      )}
                      {deliverable(o) && (
                        <Button size="sm" variant="outline-success" disabled={busyId === o._id} onClick={() => deliver(o)}><i className="bi bi-check2-circle" /> Entregado</Button>
                      )}
                      {confirmable(o) && (
                        <>
                          <Button size="sm" variant="outline-success" disabled={busyId === o._id}
                            onClick={() => setConfirming({ order: o, eta: o.deliveryEta ? new Date(o.deliveryEta).toISOString().slice(0, 10) : '' })}>
                            <i className="bi bi-check2" /> {o.status === 'confirmed' ? 'Cambiar fecha' : 'Confirmar'}
                          </Button>
                        </>
                      )}
                      {cancellable(o) && (
                        <Button size="sm" variant="outline-danger" disabled={busyId === o._id} title="Cancelar pedido" onClick={() => setCancelling({ order: o, reason: '' })}><i className="bi bi-x-lg" /></Button>
                      )}
                      {returnableOf(o) > 0 && o.status !== 'cancelled' && (
                        <Button size="sm" variant="outline-warning" disabled={busyId === o._id} title="Registrar devolución del cliente"
                          onClick={() => setNewDevolucion({ order: o, quantity: 1, reason: '' })}><i className="bi bi-arrow-return-left" /></Button>
                      )}
                      {!['delivered', 'cancelled'].includes(o.status) && (
                        <Button size="sm" variant="outline-secondary" disabled={busyId === o._id} title="Cómo se mueven los tanques"
                          onClick={() => setEditingLegs({ order: o, ...legsOf(o) })}><i className="bi bi-arrow-left-right" /></Button>
                      )}
                      {o.status === 'delivered' && isAdmin && (
                        <Button size="sm" variant="outline-secondary" disabled={busyId === o._id} onClick={() => undeliver(o)} title="Deshacer entrega"><i className="bi bi-arrow-counterclockwise" /></Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr><td colSpan={7} className="text-center text-muted py-4">{view === 'all' ? 'Todavía no hay pedidos.' : 'Nada pendiente aquí. 🎉'}</td></tr>
              )}
            </tbody>
          </Table>
        </div>
      )}

      {confirming && (
        <Modal show onHide={() => setConfirming(null)} centered>
          <Modal.Header closeButton><Modal.Title>Confirmar {confirming.order.number}</Modal.Title></Modal.Header>
          <Modal.Body>
            <p className="small text-muted">El cliente verá el pedido como confirmado en la app.</p>
            <Form.Label>Fecha estimada de entrega <span className="text-muted">(opcional)</span></Form.Label>
            <Form.Control type="date" min={todayIso()} value={confirming.eta} onChange={e => setConfirming(c => ({ ...c, eta: e.target.value }))} />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setConfirming(null)}>Volver</Button>
            <Button variant="success" disabled={busyId === confirming.order._id} onClick={submitConfirm}>Confirmar pedido</Button>
          </Modal.Footer>
        </Modal>
      )}

      {newDevolucion && (
        <Modal show onHide={() => setNewDevolucion(null)} centered>
          <Modal.Header closeButton><Modal.Title>Devolución — {newDevolucion.order.number}</Modal.Title></Modal.Header>
          <Modal.Body>
            <p className="small text-muted">
              El cliente devuelve tanques de este pedido (se quedó con {keptOf(newDevolucion.order)}). La planta la verá en "Devoluciones pendientes"
              y el inventario cambia cuando los tanques lleguen. Si ya está facturado, después te tocará la nota de crédito.
            </p>
            <Form.Label>Tanques a devolver</Form.Label>
            <Form.Control className="mb-3" type="number" min={1} max={returnableOf(newDevolucion.order)} value={newDevolucion.quantity}
              onChange={e => setNewDevolucion(s => ({ ...s, quantity: e.target.value }))} />
            <Form.Label>Motivo <span className="text-muted">(el cliente lo verá)</span></Form.Label>
            <Form.Control maxLength={300} value={newDevolucion.reason} onChange={e => setNewDevolucion(s => ({ ...s, reason: e.target.value }))} />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setNewDevolucion(null)}>Volver</Button>
            <Button variant="warning" disabled={busyId === newDevolucion.order._id} onClick={submitDevolucion}>Registrar devolución</Button>
          </Modal.Footer>
        </Modal>
      )}

      {crediting && (
        <CreditNoteModal
          invoice={crediting.invoice}
          suggestion={creditSuggestion(crediting.invoice, crediting.dev, crediting.order.number)}
          onIssued={creditIssued}
          onChanged={() => {}}
          onClose={() => setCrediting(null)}
        />
      )}

      {noBilling && (
        <Modal show onHide={() => setNoBilling(null)} centered>
          <Modal.Header closeButton><Modal.Title>Sin nota de crédito — {noBilling.order.number}</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Label>¿Por qué no hace falta?</Form.Label>
            <Form.Control maxLength={300} value={noBilling.comment} onChange={e => setNoBilling(s => ({ ...s, comment: e.target.value }))}
              placeholder="Ej.: se le repuso en el próximo despacho; no se le cobraron." />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setNoBilling(null)}>Volver</Button>
            <Button disabled={!noBilling.comment.trim()} onClick={submitNoBilling}>Guardar</Button>
          </Modal.Footer>
        </Modal>
      )}

      {editingLegs && (
        <Modal show onHide={() => setEditingLegs(null)} centered>
          <Modal.Header closeButton><Modal.Title>Forma de entrega — {editingLegs.order.number}</Modal.Title></Modal.Header>
          <Modal.Body>
            <p className="small text-muted">Lo que el cliente escogió al pedir; cámbialo si coordinaron otra cosa. El cliente verá el cambio en la app.</p>
            <Form.Label>Tanques vacíos</Form.Label>
            <Form.Select className="mb-3" value={editingLegs.empties} onChange={e => setEditingLegs(s => ({ ...s, empties: e.target.value }))}>
              {Object.entries(EMPTIES).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </Form.Select>
            <Form.Label>Tanques llenos</Form.Label>
            <Form.Select value={editingLegs.filled} onChange={e => setEditingLegs(s => ({ ...s, filled: e.target.value }))}>
              {Object.entries(FILLED).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </Form.Select>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setEditingLegs(null)}>Volver</Button>
            <Button disabled={busyId === editingLegs.order._id} onClick={submitLegs}>Guardar</Button>
          </Modal.Footer>
        </Modal>
      )}

      {cancelling && (
        <Modal show onHide={() => setCancelling(null)} centered>
          <Modal.Header closeButton><Modal.Title>Cancelar {cancelling.order.number}</Modal.Title></Modal.Header>
          <Modal.Body>
            {cancelling.order.arrivedQuantity != null && (
              <div className="alert alert-warning small">
                Sus {cancelling.order.arrivedQuantity} tanques vacíos ya están en la planta: Manifold recibirá un aviso para devolverlos o decidir qué hacer con ellos.
              </div>
            )}
            <Form.Label>Motivo <span className="text-muted">(el cliente lo verá en la app)</span></Form.Label>
            <Form.Control as="textarea" rows={3} maxLength={300} value={cancelling.reason} onChange={e => setCancelling(c => ({ ...c, reason: e.target.value }))}
              placeholder="Ej.: sin disponibilidad de nitrógeno esta semana; te llamamos para coordinar." />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setCancelling(null)}>Volver</Button>
            <Button variant="danger" disabled={!cancelling.reason.trim() || busyId === cancelling.order._id} onClick={submitCancel}>Cancelar pedido</Button>
          </Modal.Footer>
        </Modal>
      )}
    </Card>
  );
};

export default OrderRequestsPanel;
